import { useT } from "@/i18n";

// His home screen (Z Furniture `#zhome`): "Z Furniture", "Choose your section", one tile per section
// with his drawing and a line saying what it is for. His three come first in his order (Stock,
// Montage, Chariots) with his words; the app's other modules follow, drawn the same way. Only the
// person's own modules are shown.
const HIS_ORDER = ["stock", "preparation", "carts"];

// His drawings (artifact lines 384-386): `var(--accent)` is the orange, `var(--panel)` the tile's
// own background, `currentColor` the ink. The other modules get one in the same hand.
const A = "fill-orange-600", P = "fill-white", INK = "fill-current";
const ICONS: Record<string, React.ReactNode> = {
  stock: (
    <>
      <rect x="22" y="30" width="44" height="40" rx="5" className={A} /><rect x="60" y="44" width="40" height="26" rx="5" className={A} opacity=".7" />
      <rect x="30" y="10" width="34" height="22" rx="4" className={A} opacity=".5" /><rect x="30" y="40" width="18" height="11" rx="1.5" className={P} />
      <rect x="33" y="43" width="5" height="5" className={INK} /><rect x="12" y="72" width="96" height="7" rx="2" className={INK} />
      <rect x="16" y="79" width="13" height="9" className={INK} /><rect x="53" y="79" width="13" height="9" className={INK} /><rect x="91" y="79" width="13" height="9" className={INK} />
    </>
  ),
  preparation: (
    <>
      <rect x="16" y="22" width="58" height="60" rx="5" fill="none" stroke="currentColor" strokeWidth="5" />
      <line x1="16" y1="42" x2="74" y2="42" stroke="currentColor" strokeWidth="4" /><line x1="16" y1="62" x2="74" y2="62" stroke="currentColor" strokeWidth="4" />
      <rect x="38" y="30" width="14" height="5" rx="2.5" className={INK} /><rect x="38" y="50" width="14" height="5" rx="2.5" className={INK} />
      <rect x="38" y="70" width="14" height="5" rx="2.5" className={INK} /><rect x="20" y="82" width="6" height="8" className={INK} /><rect x="64" y="82" width="6" height="8" className={INK} />
      <g transform="rotate(35 96 46)"><rect x="89" y="6" width="14" height="36" rx="6" className={A} /><rect x="94" y="42" width="4" height="40" rx="1" className={INK} /></g>
    </>
  ),
  carts: (
    <>
      <rect x="18" y="22" width="34" height="30" rx="4" className={A} opacity=".75" /><rect x="54" y="30" width="30" height="22" rx="4" className={A} />
      <rect x="12" y="54" width="86" height="8" rx="2" className={INK} />
      <path d="M98 58 L98 18 L108 18" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="26" cy="74" r="8" fill="none" stroke="currentColor" strokeWidth="5" /><circle cx="84" cy="74" r="8" fill="none" stroke="currentColor" strokeWidth="5" />
      <rect x="60" y="35" width="10" height="10" className={P} /><rect x="62" y="37" width="3" height="3" className={INK} /><rect x="66" y="41" width="2" height="2" className={INK} />
    </>
  ),
  supervisor: (
    <>
      <rect x="14" y="14" width="92" height="66" rx="6" fill="none" stroke="currentColor" strokeWidth="5" />
      <rect x="28" y="50" width="12" height="20" rx="2" className={A} opacity=".5" /><rect x="48" y="38" width="12" height="32" rx="2" className={A} opacity=".75" />
      <rect x="68" y="26" width="12" height="44" rx="2" className={A} /><rect x="50" y="80" width="20" height="8" className={INK} /><rect x="36" y="86" width="48" height="5" rx="2" className={INK} />
    </>
  ),
  commercial: (
    <>
      <path d="M20 20 H62 L100 58 L66 92 L28 54 Z" className={A} /><circle cx="40" cy="38" r="7" className={P} />
      <rect x="12" y="84" width="40" height="6" rx="2" className={INK} />
    </>
  ),
  production: (
    <>
      <rect x="12" y="72" width="96" height="7" rx="2" className={INK} /><rect x="18" y="79" width="8" height="12" className={INK} /><rect x="94" y="79" width="8" height="12" className={INK} />
      <rect x="22" y="40" width="30" height="32" rx="4" className={A} opacity=".7" /><rect x="56" y="30" width="38" height="42" rx="4" className={A} />
      <rect x="64" y="10" width="22" height="16" rx="3" className={A} opacity=".5" /><path d="M30 34 L30 22 L44 22" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    </>
  ),
  receiving: (
    <>
      <path d="M26 10 H72 L90 28 V90 H26 Z" fill="none" stroke="currentColor" strokeWidth="5" strokeLinejoin="round" />
      <rect x="36" y="34" width="40" height="5" rx="2.5" className={INK} /><rect x="36" y="48" width="30" height="5" rx="2.5" className={INK} />
      <circle cx="82" cy="72" r="18" className={A} /><path d="M73 72 L80 79 L92 66" fill="none" className="stroke-white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  access: (
    <>
      <circle cx="38" cy="50" r="22" className={A} /><circle cx="38" cy="50" r="8" className={P} />
      <rect x="56" y="45" width="50" height="10" rx="3" className={INK} /><rect x="86" y="55" width="8" height="16" rx="2" className={INK} /><rect x="98" y="55" width="8" height="11" rx="2" className={INK} />
    </>
  ),
};

export default function HomePage({ tabs, onOpen }: { tabs: string[]; onOpen: (tab: string) => void }) {
  const { t } = useT();
  const ordered = [...HIS_ORDER.filter((k) => tabs.includes(k)), ...tabs.filter((k) => !HIS_ORDER.includes(k))];
  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 text-center">
        {/* His name of the app, the same in every language. */}
        <h2 className="text-4xl font-bold tracking-tight text-slate-900">{t("brand.z")} <span className="text-orange-600">{t("brand.furniture")}</span></h2>
        <p className="mt-1 text-sm text-slate-500">{t("home.choose")}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {ordered.map((k) => (
          <button key={k} onClick={() => onOpen(k)}
            className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left text-slate-800 transition hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0">
            <svg viewBox="0 0 120 100" aria-hidden="true" className="w-24 shrink-0">{ICONS[k]}</svg>
            <span>
              <span className="block text-lg font-semibold text-slate-900">{t(`nav.${k}`)}</span>
              <span className="mt-0.5 block text-sm text-slate-600">{t(`home.desc.${k}`)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
